import React, { Component } from 'react'
import PropTypes from 'prop-types'
import enhanceWithClickOutside from 'react-click-outside'
import AutocompleteSuggestions from '../AutocompleteSuggestions'
import CustomField from '../CustomField'

class AutocompleteSimple extends Component {
  static getDerivedStateFromProps(props, state) {
    if (
      state.defaultSuggestions &&
      !state.defaultSuggestions.length &&
      props.options &&
      props.options.length
    ) {
      const suggestions = props.options.sort(
        (a, b) => (a.label > b.label ? 1 : -1)
      )
      return {
        ...state,
        suggestions,
        defaultSuggestions: suggestions,
      }
    }
    return null
  }

  inputRef = React.createRef()
  state = {
    suggestions: [],
    defaultSuggestions: [],
    focusedSuggestion: 0,
    velocity: 0,
    isShowSuggestions: false,
    didSelect: false,
  }

  handleClickOutside = () => {
    this.setState({ isShowSuggestions: false })
  }

  onFocusInput = () => {
    const { defaultSuggestions } = this.state
    const state =
      defaultSuggestions && defaultSuggestions.length
        ? { suggestions: defaultSuggestions, isShowSuggestions: true }
        : { isShowSuggestions: true }
    this.setState(state)
  }

  onInput = value => {
    const { options, isCaseSensitive } = this.props
    const input = isCaseSensitive ? value : value.toLowerCase()

    const suggestions =
      (options &&
        options.reduce((collection, suggestion) => {
          const output = isCaseSensitive
            ? suggestion.label
            : suggestion.label.toLowerCase()

          if (output.includes(input)) {
            // Regexp: select every character sequence that matches the input value
            const search = new RegExp(`(${RegExp.escape(value)})`, 'gi')
            return [
              ...collection,
              {
                ...suggestion,
                emphasized: this.emphasizeSuggestion(
                  suggestion.label,
                  search,
                  input
                ),
              },
            ]
          }
          return collection
        }, [])) ||
      this.state.defaultSuggestions

    this.setState({ suggestions, focusedSuggestion: 0 })
  }

  onKeyDown = e => {
    switch (e.keyCode) {
      case 9: // Tab
      case 13: // Enter
        e.preventDefault()
        this.handleSelect()()
        break
      case 27: // Esc
        this.setState({ isShowSuggestions: false })
        this.inputRef.current.blur()
        break
      case 38: // Up
        e.preventDefault()
        this.onNavigateFocusSuggestion(-1)
        break
      case 40: // Down
        e.preventDefault()
        this.onNavigateFocusSuggestion(1)
        break
      default:
        break
    }
  }

  handleSelect = (index = this.state.focusedSuggestion) => () => {
    const { suggestions } = this.state
    const suggestion = suggestions[index]
    if (suggestion) {
      const inputEl = this.inputRef.current
      if (!inputEl) return
      inputEl.value = suggestion.label
      inputEl.blur()
      this.setState({ isShowSuggestions: false })
      this.props.onSelect({
        label: suggestion.label,
        value: suggestion.value,
      })
    }
  }

  onNavigateFocusSuggestion(velocity) {
    const { focusedSuggestion, suggestions } = this.state
    const newFocus = focusedSuggestion + velocity
    if (newFocus < suggestions.length && newFocus >= 0) {
      this.setState({
        focusedSuggestion: focusedSuggestion + velocity,
        velocity,
      })
    }
  }

  /**
   * Emphasize the matching character sequence(s).
   * @param {string} suggestion - Origin string where characters should be emphasized.
   * @param {string} search - Characters that should be emphasized.
   * @param {string} input - Original input value.
   * @return {Object} Array the origin string split with emphasized search terms.
   */
  emphasizeSuggestion(suggestion, search, input) {
    return suggestion.split(search).reduce((strings, s, index) => {
      if (s.toLowerCase() === input.toLowerCase()) {
        // Using the index as a key since a label can be duplicate and
        // there is not other way from  it.
        const key = `${suggestion}_${s}_${index}`
        return [...strings, <strong key={key}>{s}</strong>]
      }
      return s ? [...strings, s] : strings
    }, [])
  }

  getInputDimension() {
    const inputEl = this.inputRef.current
    return inputEl ? inputEl.getBoundingClientRect() : {}
  }

  render() {
    const { classes, inputFieldProps } = this.props

    const {
      suggestions,
      focusedSuggestion,
      velocity,
      isShowSuggestions,
    } = this.state

    return (
      <section className={classes.wrapper}>
        <AutocompleteSuggestions
          items={suggestions}
          focusedSuggestion={focusedSuggestion}
          onFocusSuggestion={this.handleSelect}
          inputDimension={this.getInputDimension()}
          velocity={velocity}
          isOpen={isShowSuggestions}
        />
        <CustomField
          {...inputFieldProps}
          reference={this.inputRef}
          onChange={this.onInput}
          onKeyDown={this.onKeyDown}
          onFocus={this.onFocusInput}
        />
      </section>
    )
  }
}

AutocompleteSimple.propTypes = {
  classes: PropTypes.shape({
    wrapper: PropTypes.string.isRequired,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  isCaseSensitive: PropTypes.bool,
  inputFieldProps: PropTypes.object,
}

AutocompleteSimple.defaultProps = {
  isCaseSensitive: false,
}

export default enhanceWithClickOutside(AutocompleteSimple)
